import { useState } from "react";

import type { PublicBrowseSetFacet } from "@/hooks/usePublicBrowse";
import { type BrowseRefinements, NO_REFINEMENTS } from "@/lib/browse/browseFilter";
import type { StorefrontPatch } from "@/lib/browse/storefrontFacets";

/** The facet choices behind a public storefront's Filters sheet, game pills and Location control. */
export type StorefrontFilterState = {
  game: string | null;
  /** Keyed by (game, name), since set names are unique only within a game. */
  set: PublicBrowseSetFacet | null;
  stockGroup: string | null;
  refinements: BrowseRefinements;
};

/**
 * The facet state of a public storefront list, shared by the shop (`inventory/[slug]`) and a
 * table's Pull Sheet browse (`browse/[eventId]`).
 *
 * `initial` seeds the state on mount only. `onChange` fires once per field that changes, with that
 * field alone, in the order the fields are set; the caller persists it (the table mirrors each into
 * the URL so Back from item detail restores it).
 */
export function useStorefrontFilters(
  initial: Partial<StorefrontFilterState> = {},
  onChange?: (changed: Partial<StorefrontFilterState>) => void,
) {
  const [game, setGameState] = useState(initial.game ?? null);
  const [set, setSetState] = useState(initial.set ?? null);
  const [stockGroup, setStockGroupState] = useState(initial.stockGroup ?? null);
  const [refinements, setRefinementsState] = useState(initial.refinements ?? NO_REFINEMENTS);

  const setGame = (next: string | null) => {
    setGameState(next);
    onChange?.({ game: next });
  };
  const setSet = (next: PublicBrowseSetFacet | null) => {
    setSetState(next);
    onChange?.({ set: next });
  };
  const setStockGroup = (next: string | null) => {
    setStockGroupState(next);
    onChange?.({ stockGroup: next });
  };
  const setRefinements = (next: BrowseRefinements) => {
    setRefinementsState(next);
    onChange?.({ refinements: next });
  };

  /** Applies a patch from the Filters sheet, an applied-filter chip or a game pill. */
  const applyFacetPatch = (patch: StorefrontPatch) => {
    if ("game" in patch) setGame(patch.game ?? null);
    if ("set" in patch) setSet(patch.set ?? null);
    if (patch.refinements) setRefinements({ ...refinements, ...patch.refinements });
    if ("location" in patch) setStockGroup(patch.location ?? null);
  };

  /** Clears every facet, the Location included. */
  const clear = () => {
    setGame(null);
    setSet(null);
    setStockGroup(null);
    setRefinements(NO_REFINEMENTS);
  };

  return {
    game,
    set,
    stockGroup,
    refinements,
    setGame,
    setSet,
    setStockGroup,
    setRefinements,
    applyFacetPatch,
    clear,
  };
}
